import React, { useEffect, useState, Children } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'framer-motion';
// ── RESPONSIVE HOOK ───────────────────────────────────────────────────────────
// El app está estilizado con objetos `style` en línea, por lo que las clases
// de breakpoints de Tailwind no aplican. Este hook permite ramificar estilos
// según el ancho de pantalla (móvil ≤ 640px).
export function useIsMobile(maxWidth = 640): boolean {
  const query = `(max-width: ${maxWidth}px)`;
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [isMobile, setIsMobile] = useState<boolean>(get);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return isMobile;
}
// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
export const T = {
  brand: '#FF6B01',
  // signature electric energy accent (teal-lime), used sparingly for emphasis
  accent: '#FF6B01',
  accentSoft: 'rgba(255,107,1,.12)',
  accentBorder: 'rgba(255,107,1,.32)',
  text: '#1A1207',
  textSecondary: 'rgba(26,18,7,.6)',
  textMuted: 'rgba(26,18,7,.4)',
  surface: 'rgba(0,0,0,0.02)',
  surfaceSubtle: 'rgba(0,0,0,0.04)',
  surfaceHover: 'rgba(0,0,0,0.06)',
  border: 'rgba(0,0,0,0.08)',
  borderStrong: 'rgba(0,0,0,0.18)',
  shadow: '0 8px 32px rgba(0,0,0,.08)',
  shadowSoft: '0 12px 40px rgba(0,0,0,.05)',
  innerHi: 'inset 0 1px 0 rgba(255,255,255,.6)',
  rLg: 16,
  rMd: 12,
  rSm: 8,
  ok: '#16a34a',
  okSoft: 'rgba(22,163,74,.12)',
  warn: '#d97706',
  warnSoft: 'rgba(217,119,6,.12)',
  danger: '#dc2626',
  dangerSoft: 'rgba(220,38,38,.12)',
  info: '#2563eb',
  infoSoft: 'rgba(37,99,235,.12)'
};
// ── LUX STATUS HELPER ─────────────────────────────────────────────────────────
export function luxEstado(lux: number, min: number): {
  label: string;
  color: string;
  soft: string;
} {
  if (lux < min) return {
    label: 'Fuera de rango',
    color: T.danger,
    soft: T.dangerSoft
  };
  if (lux < min * 1.2) return {
    label: 'Al límite',
    color: T.warn,
    soft: T.warnSoft
  };
  return {
    label: 'En rango',
    color: T.ok,
    soft: T.okSoft
  };
}
// ── TOKENS ────────────────────────────────────────────────────────────────────
export const glass: CSSProperties = {
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 20,
  boxShadow: T.shadowSoft
};
export const glassLight: CSSProperties = {
  background: 'rgba(255,255,255,0.6)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 16,
  boxShadow: T.shadowSoft
};
export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'rgba(0,0,0,0.03)',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: 12,
  fontSize: 14,
  color: '#1A1207',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color .2s, box-shadow .2s',
  boxSizing: 'border-box'
};
export const primaryButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  padding: '10px 20px',
  border: '1px solid rgba(255,107,1,.4)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
  color: '#ffffff',
  fontWeight: 800,
  fontSize: 13,
  cursor: 'pointer',
  letterSpacing: '0.01em',
  boxShadow: '0 4px 22px rgba(255,107,1,0.3), inset 0 1px 0 rgba(255,255,255,.25)'
};
export const ghostButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  padding: '8px 14px',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 10,
  background: 'rgba(0,0,0,0.04)',
  color: 'rgba(26,18,7,0.78)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background .15s, border-color .15s'
};
export const dangerButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  padding: '8px 14px',
  border: '1px solid rgba(220,38,38,0.3)',
  borderRadius: 10,
  background: 'rgba(220,38,38,0.1)',
  color: '#dc2626',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background .15s, border-color .15s'
};
// ── SHARED STYLE INJECTION ──────────────────────────────────────────────────
const GETR_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
  .getr-page * { font-family: 'DM Sans', sans-serif; }
  .getr-page h1, .getr-page .title { font-family: 'Space Grotesk', sans-serif; }
  .getr-btn-ghost:hover { background: rgba(0,0,0,.08) !important; border-color: rgba(0,0,0,.15) !important; }
  .getr-card:hover { transform: translateY(-3px); border-color: rgba(255,107,1,.26) !important; box-shadow: 0 16px 44px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.6) !important; }
  .getr-card { transition: transform .22s cubic-bezier(.2,.8,.2,1), border-color .22s, box-shadow .22s; }
  .getr-row:hover { background: rgba(0,0,0,.03) !important; }
  .getr-input:focus { border-color: rgba(255,107,1,.6) !important; box-shadow: 0 0 0 3px rgba(255,107,1,.14); }
  .getr-primary:hover button { filter: brightness(1.06); transform: translateY(-1px); box-shadow: 0 8px 30px rgba(255,107,1,.4), inset 0 1px 0 rgba(255,255,255,.3) !important; }
  .getr-primary button { transition: all .18s; }

  /* ── Forzar texto legible en TODOS los campos (inputs, selects, fecha) ── */
  .getr-page input,
  .getr-page select,
  .getr-page textarea {
    color: #1A1207 !important;
    color-scheme: light;
  }
  .getr-page input::placeholder,
  .getr-page textarea::placeholder {
    color: rgba(26,18,7,.45) !important;
  }
  /* Opciones del <select> (el navegador las pinta con su propio fondo claro) */
  .getr-page select option {
    color: #1A1207 !important;
    background: #ffffff !important;
  }
  /* Input de fecha: el ícono de calendario y el texto nativo */
  .getr-page input[type="date"],
  .getr-page input[type="time"],
  .getr-page input[type="datetime-local"] {
    color: #1A1207 !important;
  }
  .getr-page input[type="date"]::-webkit-calendar-picker-indicator,
  .getr-page input[type="time"]::-webkit-calendar-picker-indicator,
  .getr-page input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }
  /* Autofill del navegador no debe pintar fondo claro con texto oscuro */
  .getr-page input:-webkit-autofill {
    -webkit-text-fill-color: #1A1207 !important;
    -webkit-box-shadow: 0 0 0 1000px rgba(0,0,0,0.03) inset !important;
    caret-color: #1A1207;
  }
`;
// ── PAGE SHELL ────────────────────────────────────────────────────────────────
export function PageShell({
  title,
  sub,
  action,
  children
}: {
  title: string;
  sub: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  return <>
      <style data-id="element-1327" data-mp="8mtd0i3da9zc">{`{GETR_STYLES}`}</style>
      <div className="getr-page" data-id="element-1328" data-mp="3h00e6b4kg1nm">
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        marginBottom: 30,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 16,
        flexWrap: 'wrap'
      }} data-id="element-1329" data-mp="3h120xcuy2cl6">
          
          <div style={{
          position: 'relative'
        }} data-id="element-1330" data-mp="166xk6mw6v6wy">
            
            <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: T.accent,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: 9,
            display: 'flex',
            alignItems: 'center',
            gap: 7
          }} data-id="element-1331" data-mp="118ckcokump91">
              
              <span style={{
              width: 16,
              height: 2,
              borderRadius: 2,
              background: T.accent,
              boxShadow: `0 0 10px ${T.accent}`
            }} data-id="element-1332" data-mp="34oznkxysn9l3" />
              
              Junquito Gas
            </div>
            <div className="title" style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: 800,
            color: '#1A1207',
            letterSpacing: '-0.03em',
            fontFamily: "'Space Grotesk', sans-serif",
            lineHeight: 1.08
          }} data-id="element-1333" data-mp="3ignzp191ly22">
              
              {title}
            </div>
            <div style={{
            fontSize: 13,
            color: T.textSecondary,
            marginTop: 7,
            fontWeight: 500
          }} data-id="element-1334" data-mp="1t4vlmpg7p6t0">
              
              {sub}
            </div>
          </div>
          {action && <div className="getr-primary" data-id="element-1335" data-mp="1lr31ifm86ubw">{action}</div>}
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.06,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14
      }} data-id="element-1336" data-mp="1lprxceovbkk9">
          
          {children}
        </motion.div>
      </div>
    </>;
}
// ── MOTION GRID (staggered entrance for card grids) ───────────────────────────
export function MotionGrid({
  children,
  style
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return <motion.div style={style} initial="hidden" animate="show" variants={{
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.05
      }
    }
  }} data-id="element-1337" data-mp="1um0vzr50uf5c">
      
      {children}
    </motion.div>;
}
export function MotionItem({
  children,
  style,
  className
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return <motion.div className={className} style={style} variants={{
    hidden: {
      opacity: 0,
      y: 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.34,
        ease: [0.2, 0.8, 0.2, 1]
      }
    }
  }} data-id="element-1338" data-mp="3u3vmxhtjo6ac">
      
      {children}
    </motion.div>;
}
// ── BADGE ─────────────────────────────────────────────────────────────────────
export function Badge({
  label,
  color,
  soft,
  dot = true
}: {
  label: string;
  color: string;
  soft: string;
  dot?: boolean;
}) {
  return <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color,
    background: soft,
    border: `1px solid ${color}33`,
    padding: '4px 11px',
    borderRadius: 99,
    letterSpacing: '0.01em'
  }} data-id="element-1339" data-mp="vkf5yhhfw2vy">
      
      {dot && <span style={{
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: color,
      boxShadow: `0 0 7px ${color}`
    }} data-id="element-1340" data-mp="2sf1lfslpq2tx" />}
      {label}
    </span>;
}
// ── EMPTY STATE ───────────────────────────────────────────────────────────────
export function EmptyState({
  title,
  hint,
  accent = T.accent,
  icon
}: {
  title: string;
  hint?: string;
  accent?: string;
  icon?: ReactNode;
}) {
  return <div style={{
    ...glassLight,
    borderStyle: 'dashed',
    borderColor: 'rgba(0,0,0,.12)',
    padding: '40px 24px',
    textAlign: 'center'
  }} data-id="element-1341" data-mp="132mvphuv66ro">
      
      <div style={{
      width: 52,
      height: 52,
      margin: '0 auto 14px',
      borderRadius: 14,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `${accent}1f`,
      border: `1px solid ${accent}3a`,
      color: accent
    }} data-id="element-1342" data-mp="u4sx209mn11c">
        
        {icon ?? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1343" data-mp="29lb8pn85diw3">
          
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" data-id="element-1344" data-mp="2d2z9w8f2drha" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" data-id="element-1345" data-mp="285pdu9x66hkh" />
          </svg>}
      </div>
      <div style={{
      fontWeight: 700,
      color: T.text,
      fontSize: 15,
      marginBottom: 5
    }} data-id="element-1346" data-mp="234tapykn6zg4">
        
        {title}
      </div>
      {hint && <div style={{
      color: T.textSecondary,
      fontSize: 13,
      maxWidth: 340,
      margin: '0 auto'
    }} data-id="element-1347" data-mp="2vtgpdbavyqkp">
        
          {hint}
        </div>}
    </div>;
}
// ── SECTION TITLE ─────────────────────────────────────────────────────────────
export function SectionTitle({
  children
}: {
  children: ReactNode;
}) {
  return <div style={{
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    marginBottom: 14,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }} data-id="element-1348" data-mp="2i1aikftmhvxx">
      
      <span style={{
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: T.accent
    }} data-id="element-1349" data-mp="wac0mqo0y9nm" />
      

      {children}
    </div>;
}
// ── STAT CARD ─────────────────────────────────────────────────────────────────
export function StatCard({
  label,
  value,
  note,
  accent = T.accent
}: {
  label: string;
  value: string;
  note: string;
  accent?: string;
}) {
  const isMobile = useIsMobile();
  return <div className="getr-card" style={{
    ...glassLight,
    padding: isMobile ? '16px 16px' : '22px 24px',
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-1350" data-mp="1f9zmx2u9u3mz">
      
      {/* glow orb */}
      <div style={{
      position: 'absolute',
      right: -28,
      bottom: -28,
      width: 110,
      height: 110,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${accent}2e 0%, transparent 70%)`,
      pointerEvents: 'none'
    }} data-id="element-1351" data-mp="1oyrayzw7o3sm" />
      

      {/* accent bar */}
      <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 2,
      background: `linear-gradient(90deg, ${accent}, ${accent}00)`,
      borderRadius: '16px 16px 0 0'
    }} data-id="element-1352" data-mp="22y91pkt2hr4m" />
      

      <div style={{
      fontSize: 10,
      color: accent,
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 6
    }} data-id="element-1353" data-mp="1kcfnjm242bul">
        
        <span style={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: accent,
        boxShadow: `0 0 8px ${accent}`
      }} data-id="element-1354" data-mp="16cxrclmhjwb7" />
        

        {label}
      </div>
      <div style={{
      fontSize: isMobile ? 24 : 36,
      color: T.text,
      fontWeight: 800,
      letterSpacing: '-0.04em',
      fontFamily: "'Space Grotesk', sans-serif",
      lineHeight: 1.05,
      marginBottom: 9,
      wordBreak: 'break-word'
    }} data-id="element-1355" data-mp="1gbwszk8u2rop">
        
        {value}
      </div>
      <div style={{
      fontSize: 12,
      color: T.textMuted,
      fontWeight: 500
    }} data-id="element-1356" data-mp="2mrz343a68ch8">
        
        {note}
      </div>
    </div>;
}
// ── INFO CARD ─────────────────────────────────────────────────────────────────
export function InfoCard({
  title,
  value,
  note
}: {
  title: string;
  value: string;
  note: string;
}) {
  return <div className="getr-card" style={{
    ...glassLight,
    padding: 22,
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-1357" data-mp="2lc3ecrb12jun">
      
      <div style={{
      fontSize: 11,
      color: T.textMuted,
      fontWeight: 700,
      letterSpacing: '0.04em',
      marginBottom: 10
    }} data-id="element-1358" data-mp="3d1q23s87qqnf">
        
        {title}
      </div>
      <div style={{
      fontSize: 28,
      color: T.text,
      fontWeight: 800,
      letterSpacing: '-0.03em',
      fontFamily: "'Space Grotesk', sans-serif",
      marginBottom: 5
    }} data-id="element-1359" data-mp="3i2duoic3bfjr">
        
        {value}
      </div>
      <div style={{
      fontSize: 12,
      color: T.textSecondary,
      lineHeight: 1.4
    }} data-id="element-1360" data-mp="3w02bnf31e2jj">
        
        {note}
      </div>
    </div>;
}
// ── DATA GRID ─────────────────────────────────────────────────────────────────
export function DataGrid({
  headers,
  rows
}: {
  headers: string[];
  rows: string[][];
}) {
  return <div style={{
    ...glassLight,
    overflow: 'hidden'
  }} data-id="element-1361" data-mp="agjjhdmbw60w">
      
      <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${headers.length},1fr)`,
      background: T.surfaceSubtle,
      borderBottom: `1px solid ${T.border}`
    }} data-id="element-1362" data-mp="5g5ksm6ma8fj">
        
        {headers.map(h => <div key={h} style={{
        padding: '13px 16px',
        fontSize: 10,
        color: T.textMuted,
        fontWeight: 700,
        letterSpacing: '.1em',
        textTransform: 'uppercase'
      }} data-id="element-1363" data-mp="3royfmzozv82k">
          
            {h}
          </div>)}
      </div>
      {rows.map((r, i) => <div key={i} className="getr-row" style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${headers.length},1fr)`,
      background: i % 2 === 1 ? T.surface : 'transparent',
      borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : 'none',
      transition: 'background .15s'
    }} data-id="element-1364" data-mp="3dxtdnqy5m3p2" data-mpi={i}>
        
          {r.map((c, j) => <div key={`${i}-${j}`} style={{
        padding: '14px 16px',
        fontSize: 13,
        color: j === 0 ? T.text : T.textSecondary,
        fontWeight: j === 0 ? 600 : 400
      }} data-id="element-1365" data-mp="2twqfogjqg9mx" data-mpi={j}>
          
              {c}
            </div>)}
        </div>)}
      {rows.length === 0 && <div style={{
      padding: '28px 20px',
      textAlign: 'center',
      color: T.textMuted,
      fontSize: 13
    }} data-id="element-1366" data-mp="38rejzrded20y">
        
          Sin registros
        </div>}
    </div>;
}
// ── ACTION LIST ───────────────────────────────────────────────────────────────
export function ActionList({
  items
}: {
  items: {
    label: string;
    onClick: () => void;
  }[];
}) {
  return <div style={{
    ...glassLight,
    padding: '6px 4px'
  }} data-id="element-1367" data-mp="2ptu59d4cjju2">
      
      {items.map(item => <button key={item.label} onClick={item.onClick} className="getr-btn-ghost" style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '13px 14px',
      background: 'transparent',
      border: 'none',
      borderBottom: `1px solid ${T.border}`,
      color: T.textSecondary,
      fontSize: 13,
      fontWeight: 600,
      textAlign: 'left',
      cursor: 'pointer',
      borderRadius: 10,
      transition: 'background .15s'
    }} data-id="element-1368" data-mp="1sclp0coygbbe">
        
          <span data-id="element-1369" data-mp="3ggzr5jvltq7i">{item.label}</span>
          <span style={{
        color: T.accent,
        fontSize: 16
      }} data-id="element-1370" data-mp="3uhu1i827q18t">
          
            ›
          </span>
        </button>)}
    </div>;
}
// ── TOGGLE ────────────────────────────────────────────────────────────────────
export function Toggle({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return <button onClick={onChange} style={{
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    padding: '11px 0',
    color: checked ? T.text : T.textSecondary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'color .2s'
  }} data-id="element-1371" data-mp="35hhbjfuju2l">
      
      <span data-id="element-1372" data-mp="10no4dc69fq9k">{label}</span>
      <span style={{
      width: 40,
      height: 22,
      borderRadius: 99,
      padding: 2,
      background: checked ? 'linear-gradient(135deg,#FF8C00,#FF5A00)' : T.borderStrong,
      display: 'flex',
      justifyContent: checked ? 'flex-end' : 'flex-start',
      transition: 'all .2s',
      boxShadow: checked ? '0 2px 12px rgba(255,107,1,.5)' : 'none'
    }} data-id="element-1373" data-mp="10o67msrcb4qz">
        
        <span style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 6px rgba(0,0,0,.3)',
        transition: 'all .2s'
      }} data-id="element-1374" data-mp="hro8vre2q4l8" />
        
      </span>
    </button>;
}
// ── MODAL ─────────────────────────────────────────────────────────────────────
export function Modal({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: 'fixed',
    inset: 0,
    background: 'rgba(255,255,255,.75)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 400,
    padding: 20
  }} data-id="element-1375" data-mp="3u92x74lzphlg">
      
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.26,
      ease: [0.2, 0.8, 0.2, 1]
    }} style={{
      background: 'rgba(255,255,255,0.97)',
      border: '1px solid rgba(0,0,0,.12)',
      borderRadius: 22,
      padding: 32,
      width: 460,
      maxWidth: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 40px 100px rgba(0,0,0,.15), inset 0 1px 0 rgba(255,255,255,.6)'
    }} data-id="element-1376" data-mp="guzlwg9uj7jb">
        
        {/* Header */}
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 28,
        paddingBottom: 20,
        borderBottom: '1px solid rgba(0,0,0,.07)'
      }} data-id="element-1377" data-mp="14m50ks41vts1">
          
          <span style={{
          fontSize: 18,
          fontWeight: 800,
          color: '#1A1207',
          letterSpacing: '-0.02em',
          fontFamily: "'Space Grotesk', sans-serif"
        }} data-id="element-1378" data-mp="2z0sb3bxmbllb">
            
            {title}
          </span>
          <button onClick={onClose} style={{
          background: 'rgba(0,0,0,.04)',
          border: '1px solid rgba(0,0,0,.08)',
          borderRadius: 10,
          width: 34,
          height: 34,
          color: 'rgba(26,18,7,.5)',
          cursor: 'pointer',
          fontSize: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          transition: 'background .15s'
        }} data-id="element-1379" data-mp="26dobxnl7i1hg">
            
            ×
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>;
}
// ── PAGINATION ────────────────────────────────────────────────────────────────
// Hook reutilizable para paginar cualquier lista. Mantiene la página dentro de
// rango cuando cambian los datos o el tamaño de página.
export function usePagination<T>(items: T[], pageSize = 8) {
  const [page, setPage] = useState(1);
  // Defensa: si un caller pasa undefined (p. ej. datos aún sin cargar o una
  // forma de caché vieja), no rompemos el render al leer .length / .slice.
  const safeItems = Array.isArray(items) ? items : [];
  const total = safeItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    setPage(p => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);
  // Reinicia a la primera página si la cantidad de items cambia drásticamente.
  useEffect(() => {
    setPage(1);
  }, [pageSize]);
  const start = (page - 1) * pageSize;
  const pageItems = safeItems.slice(start, start + pageSize);
  const rangeStart = total === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + pageSize, total);
  return {
    page,
    setPage,
    totalPages,
    total,
    pageItems,
    rangeStart,
    rangeEnd,
    pageSize
  };
}
// Construye la lista de números de página con elipsis para rangos grandes.
function pageList(current: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 7) return Array.from({
    length: totalPages
  }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(totalPages - 1, current + 1);
  if (left > 2) out.push('…');
  for (let p = left; p <= right; p++) out.push(p);
  if (right < totalPages - 1) out.push('…');
  out.push(totalPages);
  return out;
}
export function Pagination({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
  label = 'registros'
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart?: number;
  rangeEnd?: number;
  total?: number;
  label?: string;
}) {
  const isMobile = useIsMobile();
  if (totalPages <= 1) return null;
  const numBtn = (active: boolean): CSSProperties => ({
    minWidth: 34,
    height: 34,
    padding: '0 10px',
    borderRadius: 10,
    border: `1px solid ${active ? T.accent : T.border}`,
    background: active ? T.accentSoft : 'rgba(255,255,255,.04)',
    color: active ? T.accent : T.textSecondary,
    fontSize: 13,
    fontWeight: active ? 800 : 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all .15s'
  });
  const arrow = (disabled: boolean): CSSProperties => ({
    width: 34,
    height: 34,
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: 'rgba(255,255,255,.04)',
    color: disabled ? T.textMuted : T.textSecondary,
    fontSize: 16,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit'
  });
  return <div style={{
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 14,
    flexWrap: 'wrap'
  }} data-id="element-1380" data-mp="12w8557gnjv58">
      
      {typeof total === 'number' && <div style={{
      fontSize: 12,
      color: T.textMuted,
      fontWeight: 500,
      order: isMobile ? 2 : 0
    }} data-id="element-1381" data-mp="1cmt10n4xylkg">
        
          {total === 0 ? `0 ${label}` : `${rangeStart}–${rangeEnd} de ${total} ${label}`}
        </div>}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      order: isMobile ? 1 : 0
    }} data-id="element-1382" data-mp="1gljrwcitd6z4">
        
        <button type="button" aria-label="Página anterior" disabled={page <= 1} onClick={() => page > 1 && onChange(page - 1)} style={arrow(page <= 1)} data-id="element-1383" data-mp="1vj2p839k4si6">
          
          ‹
        </button>
        {pageList(page, totalPages).map((p, i) => p === '…' ? <span key={`e${i}`} style={{
        color: T.textMuted,
        padding: '0 4px',
        fontSize: 13
      }} data-id="element-1384" data-mp="3scnq4zxlvnto">
          
              …
            </span> : <button key={p} type="button" aria-current={p === page ? 'page' : undefined} onClick={() => onChange(p)} style={numBtn(p === page)} data-id="element-1385" data-mp="lwyoevuh7glj">
          
              {p}
            </button>)}
        <button type="button" aria-label="Página siguiente" disabled={page >= totalPages} onClick={() => page < totalPages && onChange(page + 1)} style={arrow(page >= totalPages)} data-id="element-1386" data-mp="uu90gjf4b9ri">
          
          ›
        </button>
      </div>
    </div>;
}
// ── FIELD ─────────────────────────────────────────────────────────────────────
export function Field({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) {
  return <div style={{
    marginBottom: 18
  }} data-id="element-1387" data-mp="1il4vrqis8n3i">
      
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#1A1207',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      marginBottom: 7
    }} data-id="element-1388" data-mp="1ijtld7uu0h3f">
        
        {label}
      </div>
      {children}
    </div>;
}
// ─────────────────────────────────────────────────────────────────────────────
// CAMPOS VALIDADOS
//
// Un solo componente para todos los campos con formato. En vez de dejar
// escribir cualquier cosa y quejarse al guardar, filtra lo que no corresponde
// mientras se escribe, avisa por qué, y en los campos con formato conocido
// (placa, RNC, cédula, teléfono) va poniendo los guiones solo.
// ─────────────────────────────────────────────────────────────────────────────

export type ModoCampo = 'texto' | 'letras' | 'numeros' | 'decimal' | 'placa' | 'rnc' | 'cedula' | 'telefono' | 'codigo';

// Agrupa dígitos según un patrón: [1,2,5,1] sobre "131010001" da "1-31-01000-1".
function agrupar(digitos: string, grupos: number[], sep = '-'): string {
  const partes: string[] = [];
  let i = 0;
  for (const largo of grupos) {
    if (i >= digitos.length) break;
    partes.push(digitos.slice(i, i + largo));
    i += largo;
  }
  // Lo que sobre del último grupo se pega al final para no perder lo tecleado.
  if (i < digitos.length) partes.push(digitos.slice(i));
  return partes.join(sep);
}
type DefinicionCampo = {
  // Se prueba carácter por carácter sobre lo tecleado.
  permite: RegExp;
  // Qué se le dice al usuario cuando escribe algo que no corresponde.
  aviso: string;
  // Caracteres de formato que el propio campo pone: no cuentan como error
  // cuando vuelven a entrar al reescribir sobre el valor ya formateado.
  formato?: RegExp;
  transforma?: (limpio: string) => string;
  maximo?: number;
  teclado?: 'numeric' | 'tel';
};
const CAMPOS: Record<ModoCampo, DefinicionCampo> = {
  texto: {
    permite: /./,
    aviso: ''
  },
  // Se admiten espacios, tildes, apóstrofos y guiones porque los nombres
  // reales los llevan ("Sepúlveda", "D'Oleo", "María José").
  letras: {
    permite: /[\p{L}\s'.-]/u,
    aviso: 'Este campo solo admite letras'
  },
  numeros: {
    permite: /[0-9]/,
    aviso: 'Este campo solo admite números',
    teclado: 'numeric'
  },
  decimal: {
    permite: /[0-9.]/,
    aviso: 'Este campo solo admite números',
    teclado: 'numeric'
  },
  // Placa dominicana: letra(s) de serie y luego los dígitos, "L-317355".
  placa: {
    permite: /[A-Za-z0-9]/,
    aviso: 'La placa solo admite letras y números',
    formato: /-/,
    maximo: 12,
    transforma: v => {
      const s = v.toUpperCase();
      const letras = s.match(/^[A-Z]+/)?.[0] ?? '';
      const resto = s.slice(letras.length).replace(/[^0-9]/g, '');
      if (!letras) return resto;
      if (!resto) return letras;
      return `${letras}-${resto}`;
    }
  },
  // RNC: 9 dígitos agrupados 1-2-5-1.
  rnc: {
    permite: /[0-9]/,
    aviso: 'El RNC solo admite números',
    formato: /-/,
    maximo: 12,
    teclado: 'numeric',
    transforma: v => agrupar(v.slice(0, 9), [1, 2, 5, 1])
  },
  // Cédula: 11 dígitos agrupados 3-7-1.
  cedula: {
    permite: /[0-9]/,
    aviso: 'La cédula solo admite números',
    formato: /-/,
    maximo: 13,
    teclado: 'numeric',
    transforma: v => agrupar(v.slice(0, 11), [3, 7, 1])
  },
  // Teléfono dominicano: 10 dígitos, "(809) 247-2104".
  telefono: {
    permite: /[0-9]/,
    aviso: 'El teléfono solo admite números',
    formato: /[()\s-]/,
    maximo: 14,
    teclado: 'tel',
    transforma: v => {
      const d = v.slice(0, 10);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
  },
  // Códigos internos tipo "APP-87" o "33-21": no se les impone una posición
  // de guion porque cada empresa los numera distinto.
  codigo: {
    permite: /[A-Za-z0-9-]/,
    aviso: 'Este campo solo admite letras, números y guiones',
    transforma: v => v.toUpperCase()
  }
};
export function CampoValidado({
  modo = 'texto',
  valor,
  onChange,
  placeholder,
  style,
  maxLength,
  onEnter,
  autoFocus,
  disabled
}: {
  modo?: ModoCampo;
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
  style?: CSSProperties;
  maxLength?: number;
  onEnter?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const def = CAMPOS[modo];
  const [aviso, setAviso] = useState('');

  // El aviso se borra solo: es una corrección momentánea, no un error
  // pendiente de resolver.
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(''), 2600);
    return () => clearTimeout(t);
  }, [aviso]);
  const manejar = (bruto: string) => {
    // Los caracteres de formato que pone el propio campo se quitan antes de
    // validar; si no, al reescribir sobre "L-317355" el guion contaría como
    // carácter inválido y avisaría sin razón.
    const sinFormato = def.formato ? bruto.replace(new RegExp(def.formato.source, 'g'), '') : bruto;
    const filtrado = Array.from(sinFormato).filter(c => def.permite.test(c)).join('');
    if (filtrado.length < sinFormato.length && def.aviso) setAviso(def.aviso);
    const final = def.transforma ? def.transforma(filtrado) : filtrado;
    const tope = maxLength ?? def.maximo;
    onChange(tope ? final.slice(0, tope) : final);
  };
  return <div data-id="element-1389" data-mp="1actoyez7ihyg">
      <input style={{
      ...inputStyle,
      ...(aviso ? {
        borderColor: 'rgba(220,38,38,.55)',
        background: 'rgba(220,38,38,.05)'
      } : null),
      ...style
    }} value={valor} disabled={disabled} autoFocus={autoFocus} inputMode={def.teclado} placeholder={placeholder} onChange={e => manejar(e.target.value)} onKeyDown={e => {
      if (e.key === 'Enter' && onEnter) onEnter();
    }} data-id="element-1390" data-mp="1ad246disxzct" />

      {aviso ? <div style={{
      marginTop: 5,
      fontSize: 11.5,
      fontWeight: 600,
      color: T.danger,
      display: 'flex',
      alignItems: 'center',
      gap: 5
    }} data-id="element-1391" data-mp="1tacz5lxei6mg">

          <span style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: T.dangerSoft,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        flexShrink: 0
      }} data-id="element-1392" data-mp="2320mbamxd1i4">

            !
          </span>
          {aviso}
        </div> : null}
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIRMACIÓN
//
// Reemplaza a window.confirm, que no se puede estilizar, sale con el dominio
// del sitio arriba y se ve como un error del navegador en vez de una decisión
// del sistema.
//
// Se usa como un confirm normal, pero devolviendo una promesa:
//
//   const [dialogo, confirmar] = useConfirmacion();
//   ...
//   if (!(await confirmar({ titulo: '¿Anular la orden?', tono: 'danger' }))) return;
//   ...
//   return <>{dialogo} ...</>
// ─────────────────────────────────────────────────────────────────────────────

export type TonoConfirmacion = 'accent' | 'danger' | 'warn' | 'ok';
export interface OpcionesConfirmacion {
  titulo: string;
  mensaje?: string;
  // Datos concretos de lo que se va a hacer (orden, cantidad, destino...).
  // Se listan aparte para que se lean de un vistazo antes de decidir.
  detalles?: {
    etiqueta: string;
    valor: string;
  }[];
  // Consecuencia irreversible, si la hay.
  advertencia?: string;
  confirmar?: string;
  cancelar?: string;
  tono?: TonoConfirmacion;
  // Aviso en vez de pregunta: un solo botón, sin "Cancelar". Sirve para
  // reemplazar los alert() del navegador sin montar otro componente.
  soloAceptar?: boolean;
}
const TONOS: Record<TonoConfirmacion, {
  color: string;
  soft: string;
}> = {
  accent: {
    color: T.accent,
    soft: T.accentSoft
  },
  danger: {
    color: T.danger,
    soft: T.dangerSoft
  },
  warn: {
    color: T.warn,
    soft: T.warnSoft
  },
  ok: {
    color: T.ok,
    soft: T.okSoft
  }
};
export function useConfirmacion(): [ReactNode, (o: OpcionesConfirmacion) => Promise<boolean>] {
  const [pendiente, setPendiente] = useState<{
    opciones: OpcionesConfirmacion;
    resolver: (v: boolean) => void;
  } | null>(null);
  const isMobile = useIsMobile();
  const confirmar = (opciones: OpcionesConfirmacion) => new Promise<boolean>(resolver => setPendiente({
    opciones,
    resolver
  }));
  const cerrar = (respuesta: boolean) => {
    if (!pendiente) return;
    pendiente.resolver(respuesta);
    setPendiente(null);
  };

  // Escape cancela, igual que en el diálogo del navegador.
  useEffect(() => {
    if (!pendiente) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar(false);
      if (e.key === 'Enter') cerrar(true);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendiente]);
  const o = pendiente?.opciones;
  const tono = TONOS[o?.tono ?? 'accent'];
  const dialogo = pendiente ? <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} onClick={e => {
    if (e.target === e.currentTarget) cerrar(false);
  }} style={{
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(26,18,7,.42)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20
  }} data-id="element-1393" data-mp="5b0vjlhv62zm">

      <motion.div initial={{
      opacity: 0,
      y: 14,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.18,
      ease: [0.2, 0.8, 0.2, 1]
    }} style={{
      width: '100%',
      maxWidth: 430,
      background: '#FFFCF8',
      borderRadius: 20,
      border: `1px solid ${T.border}`,
      boxShadow: '0 24px 60px rgba(0,0,0,.22)',
      overflow: 'hidden'
    }} data-id="element-1394" data-mp="3mtcqs02ucnya">

        <div style={{
        padding: isMobile ? '24px 20px 0' : '28px 28px 0'
      }} data-id="element-1395" data-mp="33wbio6dzq7q4">
          <div style={{
          width: 46,
          height: 46,
          borderRadius: 14,
          background: tono.soft,
          border: `1px solid ${tono.color}33`,
          color: tono.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 800,
          marginBottom: 16
        }} data-id="element-1396" data-mp="2z38n5spnko7a">

            {o?.soloAceptar ? '!' : '?'}
          </div>

          <div style={{
          fontSize: 18,
          fontWeight: 800,
          color: T.text,
          lineHeight: 1.3
        }} data-id="element-1397" data-mp="cwma23ofuvce">

            {o?.titulo}
          </div>

          {o?.mensaje ? <div style={{
          marginTop: 8,
          fontSize: 14,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-1398" data-mp="gvv0ayk9sb37">

              {o.mensaje}
            </div> : null}

          {o?.detalles?.length ? <div style={{
          marginTop: 16,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          overflow: 'hidden'
        }} data-id="element-1399" data-mp="vndnbgbjr5w2">

              {o.detalles.map((d, i) => <div key={d.etiqueta} style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            padding: '9px 13px',
            fontSize: 13,
            background: i % 2 ? 'transparent' : T.surface,
            borderTop: i ? `1px solid ${T.border}` : undefined
          }} data-id="element-1400" data-mp="3gdmaqjg0g3q4" data-mpi={i}>

                  <span style={{
              color: T.textMuted
            }} data-id="element-1401" data-mp="buqg8nnoulag">{d.etiqueta}</span>
                  <span style={{
              color: T.text,
              fontWeight: 700,
              textAlign: 'right'
            }} data-id="element-1402" data-mp="6x846oedjpy8">

                    {d.valor}
                  </span>
                </div>)}
            </div> : null}

          {o?.advertencia ? <div style={{
          marginTop: 14,
          padding: '10px 13px',
          borderRadius: 10,
          background: T.warnSoft,
          border: `1px solid ${T.warn}33`,
          color: T.warn,
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.45
        }} data-id="element-1403" data-mp="um9k0wpje3dj">

              {o.advertencia}
            </div> : null}
        </div>

        <div style={{
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end',
        padding: isMobile ? 20 : '22px 28px 28px',
        flexDirection: isMobile ? 'column-reverse' : 'row'
      }} data-id="element-1404" data-mp="1idolkn9hognq">

          {o?.soloAceptar ? null : <button type="button" onClick={() => cerrar(false)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-1405" data-mp="1pubic2g8z9np">

              {o?.cancelar ?? 'Cancelar'}
            </button>}
          <button type="button" autoFocus onClick={() => cerrar(true)} style={{
          ...primaryButton,
          background: tono.color,
          borderColor: tono.color,
          boxShadow: `0 4px 18px ${tono.color}55`
        }} data-id="element-1406" data-mp="1zuc1noe49prw">

            {o?.confirmar ?? (o?.soloAceptar ? 'Entendido' : 'Confirmar')}
          </button>
        </div>
      </motion.div>
    </motion.div> : null;
  return [dialogo, confirmar];
}